import { useState, useEffect } from "react";
import { 
  getRecurringInvoices, 
  createRecurringInvoice, 
  updateRecurringInvoice,
  deleteRecurringInvoice,
  pauseRecurringInvoice,
  resumeRecurringInvoice,
  stopRecurringInvoice,
  generateRecurringInvoiceNow,
  getCustomers,
  getCatalogItems,
} from "../services/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from "../components/ui/dialog";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  RefreshCw, 
  Plus, 
  Play, 
  Pause, 
  Square, 
  Trash2, 
  Edit,
  Calendar,
  User,
  Euro,
  Zap,
  Clock
} from "lucide-react";
import { toast } from "sonner";

const FREQUENCY_LABELS = {
  daily: "Quotidienne",
  weekly: "Hebdomadaire",
  monthly: "Mensuelle",
  quarterly: "Trimestrielle",
  yearly: "Annuelle",
  custom: "Personnalisée",
};

const STATUS_BADGES = {
  active: { label: "Active", color: "bg-emerald-100 text-emerald-700" },
  paused: { label: "En pause", color: "bg-amber-100 text-amber-700" },
  stopped: { label: "Arrêtée", color: "bg-red-100 text-red-700" },
  completed: { label: "Terminée", color: "bg-slate-100 text-slate-600" },
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR"
  }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("fr-FR");
};

const RecurringInvoicesPage = () => {
  const [recurringInvoices, setRecurringInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  
  const [form, setForm] = useState({
    name: "",
    customer_id: "",
    subject: "",
    details: "",
    lines: [{ name: "", description: "", unit: "flat", quantity: 1, unit_price_ht: 0, vat_rate: 20 }],
    specific_note: "",
    payment_delay_days: 30,
    frequency: "monthly",
    frequency_value: 1,
    day_of_month: 1,
    start_date: new Date().toISOString().split("T")[0],
    end_date: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [recurringRes, customersRes, catalogRes] = await Promise.all([
        getRecurringInvoices(),
        getCustomers(),
        getCatalogItems(),
      ]);
      setRecurringInvoices(recurringRes.data);
      setCustomers(customersRes.data);
      setCatalogItems(catalogRes.data);
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setForm({
        name: item.name,
        customer_id: item.customer_id,
        subject: item.subject || "",
        details: item.details || "",
        lines: item.lines?.length > 0 ? item.lines : [{ name: "", description: "", unit: "flat", quantity: 1, unit_price_ht: 0, vat_rate: 20 }],
        specific_note: item.specific_note || "",
        payment_delay_days: item.payment_delay_days ?? 30,
        frequency: item.frequency,
        frequency_value: item.frequency_value,
        day_of_month: item.day_of_month || 1,
        start_date: item.start_date,
        end_date: item.end_date || "",
      });
    } else {
      setEditingItem(null);
      setForm({
        name: "",
        customer_id: "",
        subject: "",
        details: "",
        lines: [{ name: "", description: "", unit: "flat", quantity: 1, unit_price_ht: 0, vat_rate: 20 }],
        specific_note: "",
        payment_delay_days: 30,
        frequency: "monthly",
        frequency_value: 1,
        day_of_month: 1,
        start_date: new Date().toISOString().split("T")[0],
        end_date: "",
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.customer_id || !form.start_date) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    try {
      const payload = {
        ...form,
        end_date: form.end_date || null,
      };

      if (editingItem) {
        await updateRecurringInvoice(editingItem.id, payload);
        toast.success("Facture récurrente mise à jour");
      } else {
        await createRecurringInvoice(payload);
        toast.success("Facture récurrente créée");
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handlePause = async (id) => {
    try {
      await pauseRecurringInvoice(id);
      toast.success("Facture récurrente mise en pause");
      loadData();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleResume = async (id) => {
    try {
      await resumeRecurringInvoice(id);
      toast.success("Facture récurrente réactivée");
      loadData();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleStop = async (id) => {
    if (!window.confirm("Arrêter définitivement cette facture récurrente ?")) return;
    try {
      await stopRecurringInvoice(id);
      toast.success("Facture récurrente arrêtée");
      loadData();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette facture récurrente ?")) return;
    try {
      await deleteRecurringInvoice(id);
      toast.success("Facture récurrente supprimée");
      loadData();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleGenerateNow = async (id) => {
    try {
      const res = await generateRecurringInvoiceNow(id);
      toast.success("Facture générée ! Vous pouvez la valider depuis la liste des factures.");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la génération");
    }
  };

  const addLine = () => {
    setForm({
      ...form,
      lines: [...form.lines, { name: "", description: "", unit: "flat", quantity: 1, unit_price_ht: 0, vat_rate: 20 }]
    });
  };

  const updateLine = (index, field, value) => {
    const newLines = [...form.lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setForm({ ...form, lines: newLines });
  };

  const removeLine = (index) => {
    if (form.lines.length === 1) return;
    const newLines = form.lines.filter((_, i) => i !== index);
    setForm({ ...form, lines: newLines });
  };

  const calculateTotal = () => {
    return form.lines.reduce((sum, line) => {
      return sum + (line.quantity * line.unit_price_ht);
    }, 0);
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return "—";
    return customer.company_name || `${customer.first_name} ${customer.last_name}`.trim();
  };

  return (
    <div className="p-4 lg:p-6 xl:p-8 space-y-4 lg:space-y-6 animate-fadeIn" data-testid="recurring-invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-3">
            <RefreshCw size={28} className="text-[#26788A]" />
            Factures Récurrentes
          </h1>
          <p className="text-slate-500 mt-1 text-sm lg:text-base">
            {recurringInvoices.filter(r => r.status === "active").length} active(s) sur {recurringInvoices.length}
          </p>
        </div>
        <Button
          data-testid="new-recurring-btn"
          className="btn-primary w-full sm:w-auto"
          onClick={() => openDialog()}
        >
          <Plus size={16} className="mr-2" />
          Nouvelle récurrence
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Chargement...</div>
      ) : recurringInvoices.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="py-12 text-center">
            <RefreshCw size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 mb-4">Aucune facture récurrente</p>
            <Button className="btn-primary" onClick={() => openDialog()}>
              <Plus size={16} className="mr-2" />
              Créer votre première récurrence
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {recurringInvoices.map((item) => (
            <Card 
              key={item.id} 
              className="bg-white border-slate-200 hover:shadow-md transition-shadow"
              data-testid={`recurring-card-${item.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-slate-900">{item.name}</h3>
                    <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                      <User size={14} />
                      <span>{item.customer_name || getCustomerName(item.customer_id)}</span>
                    </div>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_BADGES[item.status]?.color || "bg-slate-100"}`}>
                    {STATUS_BADGES[item.status]?.label || item.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Clock size={14} className="text-slate-400" />
                    <span>{FREQUENCY_LABELS[item.frequency]} ({item.frequency_value}x)</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Euro size={14} className="text-slate-400" />
                    <span className="font-mono">{formatCurrency(item.total_amount_generated / Math.max(item.invoices_generated, 1))}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar size={14} className="text-slate-400" />
                    <span>Prochaine: {formatDate(item.next_invoice_date)}</span>
                  </div>
                  <div className="text-slate-500">
                    {item.invoices_generated} facture(s) générée(s)
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-3 border-t border-slate-100 flex-wrap">
                  {item.status === "active" && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-amber-600 border-amber-200 hover:bg-amber-50"
                        onClick={() => handlePause(item.id)}
                        data-testid={`pause-recurring-${item.id}`}
                      >
                        <Pause size={14} className="mr-1" />
                        Pause
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-[#26788A] border-[#26788A]/20 hover:bg-[#26788A]/5"
                        onClick={() => handleGenerateNow(item.id)}
                        data-testid={`generate-recurring-${item.id}`}
                      >
                        <Zap size={14} className="mr-1" />
                        Générer
                      </Button>
                    </>
                  )}
                  {item.status === "paused" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                      onClick={() => handleResume(item.id)}
                      data-testid={`resume-recurring-${item.id}`}
                    >
                      <Play size={14} className="mr-1" />
                      Reprendre
                    </Button>
                  )}
                  {(item.status === "active" || item.status === "paused") && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-orange-600 border-orange-200 hover:bg-orange-50"
                      onClick={() => handleStop(item.id)}
                      data-testid={`stop-recurring-${item.id}`}
                    >
                      <Square size={14} className="mr-1" />
                      Arrêter
                    </Button>
                  )}
                  <div className="flex-1" />
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-slate-600 border-slate-200 hover:bg-slate-50"
                    onClick={() => openDialog(item)}
                    data-testid={`edit-recurring-${item.id}`}
                  >
                    <Edit size={14} className="mr-1" />
                    Modifier
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-500 border-red-200 hover:bg-red-50"
                    onClick={() => handleDelete(item.id)}
                    data-testid={`delete-recurring-${item.id}`}
                  >
                    <Trash2 size={14} className="mr-1" />
                    Supprimer
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-slate-900">
              {editingItem ? "Modifier la récurrence" : "Nouvelle facture récurrente"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Nom */}
            <div>
              <Label className="text-slate-700">Nom de la récurrence *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 input-field"
                placeholder="Ex: Abonnement mensuel - Client X"
              />
            </div>

            {/* Client */}
            <div>
              <Label className="text-slate-700">Client *</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger className="mt-1.5 input-field">
                  <SelectValue placeholder="Sélectionner un client" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.company_name || `${c.first_name} ${c.last_name}`.trim()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Objet */}
            <div>
              <Label className="text-slate-700">Objet de la facture</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="mt-1.5 input-field"
                placeholder="Ex: Prestation mensuelle"
              />
            </div>

            {/* Détails */}
            <div>
              <Label className="text-slate-700">Détails (optionnel)</Label>
              <Textarea
                value={form.details}
                onChange={(e) => setForm({ ...form, details: e.target.value })}
                className="mt-1.5 input-field min-h-[60px]"
                placeholder="Détaillez ici le contenu de la prestation..."
                rows={2}
              />
            </div>

            {/* Délai de paiement */}
            <div>
              <Label className="text-slate-700">Délai de paiement</Label>
              <Select 
                value={String(form.payment_delay_days)} 
                onValueChange={(v) => setForm({ ...form, payment_delay_days: parseInt(v) })}
              >
                <SelectTrigger className="mt-1.5 input-field">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">À réception</SelectItem>
                  <SelectItem value="7">7 jours</SelectItem>
                  <SelectItem value="10">10 jours</SelectItem>
                  <SelectItem value="14">14 jours</SelectItem>
                  <SelectItem value="30">30 jours</SelectItem>
                  <SelectItem value="45">45 jours</SelectItem>
                  <SelectItem value="60">60 jours</SelectItem>
                  <SelectItem value="90">90 jours</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Fréquence */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Fréquence</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                  <SelectTrigger className="mt-1.5 input-field">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Quotidienne</SelectItem>
                    <SelectItem value="weekly">Hebdomadaire</SelectItem>
                    <SelectItem value="monthly">Mensuelle</SelectItem>
                    <SelectItem value="quarterly">Trimestrielle</SelectItem>
                    <SelectItem value="yearly">Annuelle</SelectItem>
                    <SelectItem value="custom">Personnalisée (jours)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700">
                  {form.frequency === "custom" ? "Tous les X jours" : "Répéter tous les"}
                </Label>
                <Input
                  type="number"
                  min="1"
                  value={form.frequency_value}
                  onChange={(e) => setForm({ ...form, frequency_value: parseInt(e.target.value) || 1 })}
                  className="mt-1.5 input-field"
                />
              </div>
            </div>

            {/* Jour du mois (pour mensuel) */}
            {form.frequency === "monthly" && (
              <div>
                <Label className="text-slate-700">Jour du mois (1-28)</Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  value={form.day_of_month}
                  onChange={(e) => setForm({ ...form, day_of_month: parseInt(e.target.value) || 1 })}
                  className="mt-1.5 input-field w-24"
                />
              </div>
            )}

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Date de début *</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className="mt-1.5 input-field"
                />
              </div>
              <div>
                <Label className="text-slate-700">Date de fin (optionnel)</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  className="mt-1.5 input-field"
                />
              </div>
            </div>

            {/* Lignes */}
            <div>
              <Label className="text-slate-700 mb-2 block">Lignes de facture</Label>
              <div className="space-y-3">
                {form.lines.map((line, index) => (
                  <div key={index} className="flex gap-2 items-start p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1 space-y-2">
                      <Input
                        value={line.name}
                        onChange={(e) => updateLine(index, "name", e.target.value)}
                        placeholder="Désignation"
                        className="input-field"
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <Input
                          type="number"
                          value={line.quantity}
                          onChange={(e) => updateLine(index, "quantity", parseFloat(e.target.value) || 0)}
                          placeholder="Qté"
                          className="input-field"
                        />
                        <Input
                          type="number"
                          value={line.unit_price_ht}
                          onChange={(e) => updateLine(index, "unit_price_ht", parseFloat(e.target.value) || 0)}
                          placeholder="Prix HT"
                          className="input-field"
                        />
                        <Select value={String(line.vat_rate)} onValueChange={(v) => updateLine(index, "vat_rate", parseFloat(v))}>
                          <SelectTrigger className="input-field">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="20">20%</SelectItem>
                            <SelectItem value="10">10%</SelectItem>
                            <SelectItem value="5.5">5.5%</SelectItem>
                            <SelectItem value="0">0%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {form.lines.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-600"
                        onClick={() => removeLine(index)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={addLine}
              >
                <Plus size={14} className="mr-1" />
                Ajouter une ligne
              </Button>
              <div className="mt-3 text-right">
                <span className="text-slate-600">Total HT: </span>
                <span className="font-semibold text-slate-900 font-mono">{formatCurrency(calculateTotal())}</span>
              </div>
            </div>

            {/* Note */}
            <div>
              <Label className="text-slate-700">Note (optionnel)</Label>
              <Textarea
                value={form.specific_note}
                onChange={(e) => setForm({ ...form, specific_note: e.target.value })}
                className="mt-1.5 input-field"
                placeholder="Note qui apparaîtra sur chaque facture générée"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="btn-outline">
              Annuler
            </Button>
            <Button onClick={handleSubmit} className="btn-primary">
              {editingItem ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecurringInvoicesPage;
